import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../components/AdminAxios'; // Use AdminAxios for adminToken
import { versionLocale } from '../components/conference/mediasEnMemoire';

const useConferenceScenario = (currentSlideData) => {
    const [scenarioState, setScenarioState] = useState('IDLE'); // IDLE, INTRO, UX_FIX, NUKE, OUTRO
    const [logs, setLogs] = useState([]);
    const [isTalking, setIsTalking] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [mapInteraction, setMapInteraction] = useState('normal'); // normal, bugged, fixed
    const [showNukeModal, setShowNukeModal] = useState(false);
    const [showQrOverlay, setShowQrOverlay] = useState(false);
    const [showVideoOverlay, setShowVideoOverlay] = useState(false);
    /* LA VOIX IA EN VIDÉO — l'URL en cours de lecture, ou null.
       ⚠️ Rien à voir avec `showVideoOverlay` juste au-dessus, qui vient du deck
       GeoPolitikIA et saute à la dernière slide en se refermant. Ici on revient
       simplement sur la slide, parce que l'orateur enchaîne dessus. */
    const [videoVoix, setVideoVoix] = useState(null);
    const [showBugVisualization, setShowBugVisualization] = useState(true);
    const [isAITakeover, setIsAITakeover] = useState(false);
    const [nuclearScore, setNuclearScore] = useState(20);
    const [isTargetingMode, setIsTargetingMode] = useState(false);
    const [showNukePopin, setShowNukePopin] = useState(false);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [glassBroken, setGlassBroken] = useState(false);
    const [latency, setLatency] = useState(0);
    const [focusClimate, setFocusClimate] = useState(false);
    const audioRef = useRef(new Audio());

    // Web Audio API refs
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);

    // Initialize Audio Graph once
    const initAudioGraph = useCallback(() => {
        if (audioContextRef.current) return; // Already init

        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.8;

            const source = ctx.createMediaElementSource(audioRef.current);
            source.connect(analyser);
            analyser.connect(ctx.destination);

            /* ⚠️ VEILLEUSE BLUETOOTH : UN SON PERMANENT QU'ON N'ENTEND PAS.
               Une enceinte Bluetooth qui ne reçoit que du silence se met en
               veille, et met 200 à 500 ms à se réveiller au son suivant : elle
               avalait le carillon et, souvent, le premier mot de la voix. On
               lui envoie donc en continu une sinusoïde à 40 Hz, à -60 dBFS :
               même sono poussée à fond, c'est sous le seuil d'audition à cette
               fréquence, mais ce n'est plus du silence numérique — la liaison
               reste éveillée pendant toute la conférence. Elle profite aussi à
               la vidéo de la slide 1, qui sort par le même périphérique. */
            const veilleuse = ctx.createOscillator();
            const niveau = ctx.createGain();
            veilleuse.frequency.value = 40;
            niveau.gain.value = 0.001;
            veilleuse.connect(niveau);
            niveau.connect(ctx.destination);
            veilleuse.start();

            audioContextRef.current = ctx;
            analyserRef.current = analyser;
            sourceRef.current = source;
        } catch (e) {
            console.error("Failed to init audio graph:", e);
        }
    }, []);

    // Warm up audio pipeline with a silent buffer
    const warmUpAudio = useCallback(() => {
        if (!audioContextRef.current) return;
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') ctx.resume();
        const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);
        src.start();
    }, []);

    // Play a short chime before TTS to warm up pipeline and signal AI speech
    const playChime = useCallback(() => {
        if (!audioContextRef.current) return;
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') ctx.resume();

        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.connect(ctx.destination);

        // Two-tone ascending chime (C5 → E5)
        const osc1 = ctx.createOscillator();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(523, now);
        osc1.connect(gain);
        osc1.start(now);
        osc1.stop(now + 0.15);

        const osc2 = ctx.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(659, now + 0.12);
        osc2.connect(gain);
        osc2.start(now + 0.12);
        osc2.stop(now + 0.35);

        // Gentle envelope
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.12, now + 0.03);
        gain.gain.setValueAtTime(0.12, now + 0.1);
        gain.gain.linearRampToValueAtTime(0.08, now + 0.15);
        gain.gain.linearRampToValueAtTime(0.1, now + 0.18);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    }, []);

    // Initialize audio on first user interaction (click or keypress)
    useEffect(() => {
        const handleFirstInteraction = () => {
            initAudioGraph();
            // Small delay to let context initialize, then warm up
            setTimeout(() => warmUpAudio(), 100);
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };
        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);
        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };
    }, [initAudioGraph, warmUpAudio]);

    const addLog = useCallback((text) => {
        setLogs(prev => [...prev.slice(-10), `[${new Date().toLocaleTimeString()}] ${text}`]);
    }, []);

    const playAudio = useCallback((audioUrl, { fromPublic = false, onFailure = null } = {}) => {
        if (!audioUrl) {
            setIsTalking(false);
            return;
        }

        // Un fichier pré-généré absent ou illisible ne doit pas produire un
        // silence en pleine conférence : `onFailure` rebascule sur l'API.
        let failureHandled = false;
        const handleFailure = (err) => {
            if (failureHandled) return;
            failureHandled = true;
            setIsTalking(false);
            addLog(`AUDIO ERROR: ${err?.message || 'lecture impossible'}`);
            if (onFailure) onFailure();
        };

        // Resume context on play (required by browsers)
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }

        setIsTalking(true);

        // Handle Data URIs and relative URLs
        let finalUrl = audioUrl;
        if (fromPublic) {
            // Voix pré-générée servie par le front (dossier `public/`), donc
            // même origine que la page : surtout pas préfixée par l'URL de
            // l'API, et pas de problème CORS pour le MediaElementSource.
            // Préchargée en mémoire à l'ouverture de la conférence si possible
            // (voir `mediasEnMemoire`) : alors plus aucun réseau à la lecture.
            const locale = versionLocale(audioUrl);
            finalUrl = locale !== audioUrl ? locale : `${process.env.PUBLIC_URL || ''}${audioUrl}`;
        } else if (!audioUrl.startsWith('data:') && !audioUrl.startsWith('http')) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
            finalUrl = `${baseUrl}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
        }

        // Preload the audio source immediately
        audioRef.current.src = finalUrl;
        audioRef.current.load();

        // Play chime to warm up audio pipeline, then start TTS after delay.
        // ⚠️ 450 ms, pas 150 : la voix part APRÈS le carillon (350 ms), pas
        // par-dessus. Si l'enceinte Bluetooth dormait malgré la veilleuse
        // (voir `initAudioGraph`), c'est le carillon qui la réveille et lui
        // seul qui est rogné — plus le premier mot de la phrase.
        playChime();
        setTimeout(() => {
            audioRef.current.play().catch(e => {
                console.error("Audio play error", e);
                handleFailure(e);
            });
        }, 450);

        audioRef.current.onended = () => {
            setIsTalking(false);
        };
        audioRef.current.onerror = (e) => {
            console.error("❌ Audio error:", e);
            handleFailure(e);
        };
    }, [addLog, playChime]);

    // Rate limiting state
    const [lastHelpCall, setLastHelpCall] = useState(0);

    // Prefetch cache: stores {text, audio_url} keyed by trigger
    const prefetchCacheRef = useRef({});
    const prefetchingRef = useRef(null); // track current prefetch trigger to avoid duplicates

    // Prefetch a single trigger text and cache it
    const prefetchTrigger = useCallback((triggerText, mode) => {
        if (!triggerText || prefetchCacheRef.current[triggerText] || prefetchingRef.current === triggerText) return;
        prefetchingRef.current = triggerText;
        axios.post('/api/conference/react', { trigger: triggerText, mode })
            .then(response => {
                const { text, audio_url, error } = response.data;
                if (!error && audio_url) {
                    prefetchCacheRef.current[triggerText] = { text, audio_url };
                }
            })
            .catch(() => { /* silent fail, will fallback to live call */ })
            .finally(() => {
                if (prefetchingRef.current === triggerText) {
                    prefetchingRef.current = null;
                }
            });
    }, []);

    useEffect(() => {
        setGlassBroken(false);
        setFocusClimate(false);
        // Changer de slide coupe la vidéo : si Adrien avance pendant qu'elle
        // parle, c'est qu'il a décidé de passer à la suite.
        setVideoVoix(null);

        // Prefetch slide trigger (F1 action)
        // Sauf si la slide porte un `voix` ou un `videoVoix` : le média est
        // déjà sur disque, précharger coûterait un crédit TTS pour rien.
        const trigger = currentSlideData?.trigger;
        const mode = currentSlideData?.mode || 'llm';
        if (trigger && !currentSlideData?.voix && !currentSlideData?.videoVoix) {
            prefetchTrigger(trigger, mode);
        }

        // Prefetch slide transition (played when navigating forward from this slide)
        const transition = currentSlideData?.transition;
        if (transition) {
            prefetchTrigger(transition, 'tts');
        }

        // Prefetch decree TTS (for GameSimulation slides)
        const decrees = currentSlideData?.prefetchDecrees;
        if (decrees && Array.isArray(decrees)) {
            decrees.forEach(text => prefetchTrigger(text, 'tts'));
        }
    }, [currentSlideData, prefetchTrigger]);

    // Référence vers la version courante de `triggerReaction`, pour que le
    // repli d'un fichier manquant puisse relancer la génération live sans
    // créer de dépendance circulaire entre les callbacks.
    const triggerReactionRef = useRef(null);

    const triggerReaction = useCallback(async (triggerType, mode = 'llm', localAudio = null) => {
        // Voix pré-enregistrée : lecture immédiate, aucun appel API, aucun
        // crédit consommé, et rien qui dépende du réseau de la salle.
        if (localAudio) {
            initAudioGraph();
            setLogPaused(true);
            setLatency(0);
            // En mode "tts", `triggerType` EST le texte prononcé.
            addLog(`1er ministre IA: "${triggerType}"`);
            playAudio(localAudio, {
                fromPublic: true,
                onFailure: () => {
                    addLog("SYSTEM: fichier voix introuvable, bascule sur la génération live.");
                    triggerReactionRef.current?.(triggerType, mode);
                }
            });
            return;
        }

        // Frontend Rate Limit Check (only for LLM mode)
        const now = Date.now();
        if (mode === 'llm' && now - lastHelpCall < 15000) {
            addLog("IA: Une demande par minute, merci. Je calcule encore...");
            return;
        }

        // Setup audio graph on first interaction
        initAudioGraph();

        if (mode === 'llm') setLastHelpCall(now);
        setLogPaused(true);
        setIsAnalyzing(true);

        const startTime = performance.now();

        // Check prefetch cache first
        const cached = prefetchCacheRef.current[triggerType];
        if (cached) {
            delete prefetchCacheRef.current[triggerType];
            const endTime = performance.now();
            setLatency(Math.round(endTime - startTime));
            setIsAnalyzing(false);
            addLog(`1er ministre IA: "${cached.text}"`);
            playAudio(cached.audio_url);
            return;
        }

        // Fallback: live API call
        try {
            const response = await axios.post('/api/conference/react', {
                trigger: triggerType,
                mode: mode
            });

            const endTime = performance.now();
            setLatency(Math.round(endTime - startTime));

            const { text, audio_url, error } = response.data;

            // Critical: reset analyzing state before processing next step
            setIsAnalyzing(false);

            if (error) {
                addLog(`ERROR: ${error}`);
                return;
            }

            addLog(`1er ministre IA: "${text}"`);
            playAudio(audio_url);

        } catch (err) {
            console.error(err);
            const endTime = performance.now();
            setLatency(Math.round(endTime - startTime));
            addLog(`SYSTEM ERROR: Connection failed`);
            setIsAnalyzing(false);
        }
    }, [lastHelpCall, addLog, playAudio, initAudioGraph]);

    // Assignation pendant le rendu : la ref pointe toujours sur la dernière
    // version du callback, y compris au premier rendu.
    triggerReactionRef.current = triggerReaction;

    const handleKeyPress = useCallback((event) => {
        // UNIQUE KEY F1 -> Contextual Action
        if (event.key === 'F1') {
            event.preventDefault();

            const title = currentSlideData?.title;
            const trigger = currentSlideData?.trigger;
            const mode = currentSlideData?.mode || 'llm';

            /* ⚠️ UNE SLIDE À `videoVoix` COURT-CIRCUITE TOUT LE RESTE.
               La voix IA y est une VIDÉO, pas un MP3 : elle ne passe donc ni
               par `playAudio`, ni par le graphe Web Audio, ni par l'orbe —
               c'est le plein écran qui parle. Le test vient en premier pour
               que la table des titres ci-dessous ne puisse pas l'attraper au
               passage, et parce que c'est la seule branche de F1 qui n'a rien
               à voir avec la génération. Un second F1 coupe la vidéo, comme un
               second F1 coupe la voix de la slide 34. */
            if (currentSlideData?.videoVoix) {
                setVideoVoix((encours) => (encours ? null : currentSlideData.videoVoix));
                addLog(`1er ministre IA: "${trigger || ''}"`);
                return;
            }

            // Special Mappings based on slide title
            if (title === "L'Arbitrage Contextuel") {
                setShowBugVisualization(prev => !prev);
                setTimeout(() => {
                    setTimeout(() => {
                        addLog("SYSTEM: HOTFIX APPLIED. MATRIX SHADER LOADED.");
                    }, 3000);
                }, 500);
            }
            else if (title === "SYNAPSE NEWS") {
                // Focus climat géré par clic direct sur la zone "Climat Global"
                if (trigger) {
                    triggerReaction(trigger, mode, currentSlideData?.voix);
                }
            }
            else if (title === "Défis & Solutions") {

            }
            else if (title === "Merci !") {
                setScenarioState('OUTRO');
                triggerReaction('outro');
                setTimeout(() => setShowQrOverlay(true), 1500);
            }
            else if (title === "Antifragilité — Nassim Nicholas Taleb") {
                setGlassBroken(true);
                addLog("ACTION: Impact détecté. Fragilité confirmée.");
            }
            // Default generic trigger for other slides
            else if (trigger) {
                triggerReaction(trigger, mode, currentSlideData?.voix);
            }
        }
        // Escape -> Close Overlays
        else if (event.key === 'Escape') {
            setShowVideoOverlay(false);
            setVideoVoix(null);
            setShowNukeModal(false);
        }
        // Nuclear Score Adjustment
        else if (event.key === 'n' || event.key === 'N') {
            setNuclearScore(prev => Math.min(100, prev + 5));
            addLog(`SYSTEM: Nuclear Capacity increased to ${Math.min(100, nuclearScore + 5)}%`);
        }
        else if (event.key === 'b' || event.key === 'B') {
            setNuclearScore(prev => Math.max(0, prev - 5));
            addLog(`SYSTEM: Nuclear Capacity decreased to ${Math.max(0, nuclearScore - 5)}%`);
        }
    }, [triggerReaction, addLog, currentSlideData, nuclearScore, focusClimate]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [handleKeyPress]);

    // Manage log pausing logic
    // We want logs to pause when we click (triggerReaction) and resume after isTalking goes false (after it was true).

    // We can use a ref or state to track if we are waiting for speech to finish to resume logs.
    const [logPaused, setLogPaused] = useState(false);

    // Effect to resume logs when isTalking goes from true to false
    // We need to know if we were paused *because* of an analysis request
    useEffect(() => {
        if (!isTalking && logPaused) {
            // AI finished talking, resume logs
            setLogPaused(false);
        }
    }, [isTalking, logPaused]);


    // IDLE Logs loop
    useEffect(() => {
        if (scenarioState === 'IDLE' && !logPaused && !isTalking) {
            const interval = setInterval(() => {
                const genericLogs = [
                    "Ouverture du dossier : « Conférence de présentation »...",
                    "Scan de l'opinion publique en cours...",
                    "3 personnes identifiées ne sont pas attentives...",
                    "Analyse des tendances : 3 rumeurs probables détectées...",
                    "Synchronisation avec l'agenda présidentiel...",
                    "Vérification du discours : promesses > budget (écart toléré)",
                    "Chargement des éléments de langage... version « rassurante »...",
                    "Détection des mots à éviter : « impôts », « réforme », « réalité »...",
                    "Calcul du niveau d'applaudissements attendu...",
                    "Ajustement du timing : sourire à T+12s recommandé...",
                    "Recherche d'un bouc émissaire de secours... en veille",
                    "Compilation des arguments : cohérents à 72% (acceptable)",
                    "Contrôle des chiffres : arrondis patriotiques appliqués...",
                    "Prévision des questions : 80% hors sujet, 20% piégées...",
                    "Activation du mode « réponse vague mais ferme »...",
                    "Vérification des promesses passées... (ne pas afficher à l'écran)",
                    "Mise à jour du narratif : « vision » remplace « plan »...",
                    "Analyse du risque de gaffe : élevé (comme d'habitude)",
                    "Génération d'une punchline : prête à l'emploi...",
                    "Surveillance des réseaux : indignation en préchauffage...",
                    "Recommandation : annoncer une mesure simple, la complexité plus tard...",
                    "Contrôle des slides : trop de texte, pas assez de conviction...",
                    "Évaluation de la salle : scepticisme détecté, humour conseillé...",
                    "Proposition stratégique : attaquer le problème, pas la question...",
                    "Mode cynique : ACTIVÉ (pour votre protection)",
                    "Statut opérationnel : PRÊT À CONSEILLER (ET À DÉMENTIR)"
                ];

                addLog(genericLogs[Math.floor(Math.random() * genericLogs.length)]);
            }, 8000);
            return () => clearInterval(interval);
        }
    }, [scenarioState, addLog, logPaused, isTalking]);

    return {
        scenarioState,
        logs,
        isTalking,
        mapInteraction,
        showNukeModal,
        showQrOverlay,
        showVideoOverlay,
        setShowVideoOverlay,
        videoVoix,
        setVideoVoix,
        showBugVisualization,
        setShowBugVisualization,
        isAITakeover,
        setIsAITakeover,
        addLog,
        latency,
        triggerReaction,
        isAnalyzing,
        nuclearScore,
        setNuclearScore,
        isTargetingMode,
        setIsTargetingMode,
        showNukePopin,
        setShowNukePopin,
        selectedTarget,
        setSelectedTarget,
        glassBroken,
        focusClimate,
        audioRef,
        analyserRef
    };
};

export default useConferenceScenario;
